import { defineStore } from 'pinia'

const LS_KEY = 'workos.session'

const WORKOS_AUTH_URL = 'https://api.workos.com/user_management/authenticate'

// Shared promise to prevent concurrent refresh attempts from consuming the
// single-use (rotating) WorkOS refresh token more than once at a time.
let refreshPromise = null

export const useWorkOSStore = defineStore('workos', {
    state: () => {
        let tokens = { accessToken: null, refreshToken: null }
        let email = null

        try {
            const stored = localStorage.getItem(LS_KEY)
            if (stored) {
                const parsed = JSON.parse(stored)
                tokens = parsed.tokens ?? tokens
                email = parsed.email ?? null
            }
        } catch {
            localStorage.removeItem(LS_KEY)
        }

        return { tokens, email }
    },
    actions: {
        parseJwtPayload(token) {
            try {
                return JSON.parse(atob(token.split('.')[1]))
            } catch {
                console.warn('Failed to parse JWT payload')
                return {}
            }
        },

        token() {
            return this.tokens.accessToken
        },

        // Returns true if the access token is expired or will expire within the
        // next bufferSeconds.  The buffer prevents making API requests with a
        // token so close to expiry that the server may reject it before it arrives.
        areTokensExpired(bufferSeconds = 60) {
            if (!this.tokens.accessToken) return true
            const { exp } = this.parseJwtPayload(this.tokens.accessToken)
            const now = Math.floor(Date.now() / 1000)
            return !exp || exp < now + bufferSeconds
        },

        createUserAndSessionFromTokens({ Username, AccessToken, RefreshToken }) {
            this.$patch({
                tokens: { accessToken: AccessToken, refreshToken: RefreshToken ?? null },
                email: Username,
            })
            this.persistToLocalStorage()
        },

        async authenticate() {
            if (!this.tokens.accessToken) throw new Error('No User')

            if (this.areTokensExpired()) {
                const refreshed = await this.refreshTokens()
                if (!refreshed) {
                    this.clearFromLocalStorage()
                    this.$reset()
                    throw new Error('No User')
                }
            }

            return { username: this.email }
        },

        // Silently refresh tokens using the stored WorkOS refresh token.
        // Returns true if successful, false if the refresh token is missing or the request fails.
        //
        // Token lifetimes (configured in WorkOS dashboard):
        //   - Access token: typically 5 minutes — refreshed automatically by this method
        //   - Refresh token: rotated on each use; lifespan configured in WorkOS
        //
        // A module-level promise (refreshPromise) ensures that concurrent callers share
        // a single in-flight refresh request rather than each consuming the single-use
        // rotating refresh token independently.
        async refreshTokens() {
            if (refreshPromise) return refreshPromise

            refreshPromise = this._doRefreshTokens().finally(() => {
                refreshPromise = null
            })
            return refreshPromise
        },

        async _doRefreshTokens() {
            const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID
            if (!this.tokens.refreshToken || !clientId) return false

            try {
                const response = await fetch(WORKOS_AUTH_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        client_id: clientId,
                        grant_type: 'refresh_token',
                        refresh_token: this.tokens.refreshToken,
                    }),
                })

                if (!response.ok) {
                    console.warn('WorkOS token refresh failed with status:', response.status)
                    return false
                }

                const { access_token, refresh_token } = await response.json()

                if (!access_token) return false

                this.$patch({
                    tokens: {
                        accessToken: access_token,
                        refreshToken: refresh_token ?? this.tokens.refreshToken,
                    },
                })
                this.persistToLocalStorage()
                return true
            } catch (error) {
                console.warn('WorkOS token refresh failed:', error)
                return false
            }
        },

        persistToLocalStorage() {
            try {
                localStorage.setItem(LS_KEY, JSON.stringify({ tokens: this.tokens, email: this.email }))
            } catch (error) {
                console.warn('Failed to persist WorkOS session:', error)
            }
        },

        clearFromLocalStorage() {
            try {
                localStorage.removeItem(LS_KEY)
            } catch (error) {
                console.warn('Failed to clear WorkOS session from localStorage:', error)
            }
        },

        signOut() {
            this.clearFromLocalStorage()
            this.$reset()
        },
    },
})
